// @ts-check
import Vue from 'vue';
import GetForecastTricastOdds from '@bto-services/api/shared/getForeTricastOdds';
import { RACE_CAST_ITEM_TYPE, RACE_MARKET_ID, RACING_BETTING_TYPES } from 'bto-models/match/races/const';
import { debounce } from '@bto-services/utils';
import { TOAST_NOTIFICATION_MESSAGE_STATUS, getToastAdditionalTime } from 'bto-models/toast-notification';
import { setToastNotification } from '@bto-services/toast-notification';


export const raceCastState = Vue.observable(getDefaultState());

/**
 * 
 * @returns {import('bto-models/match/races/interfaces').IRaceCastStore}
 */
function getDefaultState() {
  return {
    regular: [{}, {}, {}],
    any: [],
    betType: 2,
    raceOdd: {},
    runnersDetails: [],
    loading: false,
  }
}

/**
 * Convert regualr race item into runner details for api call
 * @param {import('bto-models/match/races/interfaces').RaceCastRunner} item - regualr race item
 * @returns 
 */
function getItem(item) {
  return {
    ExternalRunnerID: item.externalRunnerID,
    CompetitorID: item.competitorId,
    Order: item.Order,
    Name: item.name || item.competitorName,
  }
}

/**
 * @type {GetForecastTricastOdds | null} 
 */
let extCall = null;

/**
 * Toggle race cast items in forecast and tricast mode, and get odds if we can 
 * @param {import('bto-models/match/races/interfaces').RaceCastRunner} item - race item
 * @param {number} outrightID - race outright id
 * @param {3|2} betType - race cast mode (3-tricast,2-forecast)
 */
export function toggleCastItem(item, outrightID, betType) {
  if (extCall) {
    extCall.abort();
    extCall = null;
  }
  raceCastState.loading = false;
  raceCastState.raceOdd = {};
  raceCastState.runnersDetails = [];
  raceCastState.betType = betType;

  if (item.type === RACE_CAST_ITEM_TYPE.REGULAR) {
    raceCastState.any = [];
    const idx = (+item.Order) - 1;
    const sameItem = raceCastState.regular[idx] && raceCastState.regular[idx].id === item.id;
    /** @type any */
    const newReg = raceCastState.regular.map(ri => !(ri.id === item.id || ri.Order === item.Order) ? ri : {});
    if (!sameItem) {
      newReg[idx] = item;
    }
    raceCastState.regular = newReg;
    const filterdItems = raceCastState.regular.filter(ri => ri.id);
    const checkPrice = filterdItems.length === raceCastState.betType;
    if (checkPrice) {
      const OddTypeID = raceCastState.betType === RACING_BETTING_TYPES.TRICAST ? RACE_MARKET_ID.TRICAST : RACE_MARKET_ID.FORECAST;
      // ts-ignore-next-line
      getCastOdds(outrightID, OddTypeID, /** @type {any[]} */(filterdItems));
    }
    return;
  }
  if (item.type === RACE_CAST_ITEM_TYPE.ANY) {
    raceCastState.regular = [{}, {}, {}];
    const newAny = raceCastState.any.filter(ai => ai.id !== item.id);
    // We toggled the same any from list
    if (newAny.length < raceCastState.any.length) {
      raceCastState.any = newAny;
      return;
    }
    // If we have max items for betType remove first item from list
    if (raceCastState.any.length === raceCastState.betType) {
      newAny.shift();
    }
    newAny.push(item);
    raceCastState.any = newAny;
    if (raceCastState.any.length === raceCastState.betType) {
      const OddTypeID = raceCastState.betType === RACING_BETTING_TYPES.TRICAST ? RACE_MARKET_ID.TRICAST_ANY : RACE_MARKET_ID.FORECAST_ANY;
      getCastOdds(outrightID, OddTypeID, raceCastState.any);
    }
  }
}

/**
 * @type {import('bto-models/match/races/interfaces').TGetRaceOddsFn} 
 */
const getCastOdds = debounce(/**  @type {import('bto-models/match/races/interfaces').TGetRaceOddsFn}*/
  (outrightID, OddTypeID, items) => {
    let getForecastTricastOdds = new GetForecastTricastOdds();
    const runnersData = items.map((i) => getItem(i));
    const oddsParams = {
      OutrightID: +outrightID,
      OddTypeID: OddTypeID,
      RunnersData: runnersData,
    };
    getForecastTricastOdds.postParams = oddsParams;
    raceCastState.loading = true;
    getForecastTricastOdds.execute().then((resp) => {
      if (resp.data && resp.data.cmid) {
        raceCastState.raceOdd = resp.data;
        raceCastState.raceOdd.oddTypeID = OddTypeID;
        raceCastState.runnersDetails = runnersData;
        return;
      }
      raceCastState.raceOdd = {};
      raceCastState.runnersDetails = [];
      const expireTime = Date.now() + getToastAdditionalTime();
      const key = `${APP_SETTINGS.player.id}_race_success`;
      setToastNotification(key, {
        playerId: APP_SETTINGS.player.id,
        expireTime,
        message: 'Public.NoOddsForSelections',
        status: TOAST_NOTIFICATION_MESSAGE_STATUS.ERROR,
      });
    }).catch(() => {
      raceCastState.raceOdd = {};
      raceCastState.runnersDetails = [];
    }).finally(() => {
      extCall = null;
      raceCastState.loading = false;
    });
    extCall = getForecastTricastOdds;
  }, 600);

export function resetRaceCast() {
  if (extCall) {
    extCall.abort();
    extCall = null;
  }
  Object.assign(raceCastState, getDefaultState());
}